import { wrapLine, pdfEscapeLiteral, encodeLatin1, concatBytes } from "./stringUtils";
import { toAsciiPdfText } from "./asciiPdfText";

const PAGE_BREAK = "__PAGE_BREAK__";
const IMAGE_PREFIX = "__IMAGE__:";
const SECTION_PREFIX = "__SECTION__:";
const COVER_MARKER = "__COVER__";

const BRAND = {
  purple: [0.69, 0.32, 1.0],
  purpleDark: [0.07, 0.03, 0.125],
  purpleMid: [0.18, 0.08, 0.28],
  blue: [0.16, 0.66, 1.0],
  white: [1, 1, 1],
  text: [0.11, 0.11, 0.13],
  muted: [0.38, 0.38, 0.44],
  card: [0.95, 0.94, 0.98],
  cardBorder: [0.88, 0.86, 0.92],
};

function scaleImage(img, maxW) {
  const iw = img.width;
  const ih = img.height;
  if (!iw || !ih) return null;
  const scale = Math.min(1, maxW / iw);
  const w = Math.max(1, Math.round(iw * scale));
  const h = Math.max(1, Math.round(ih * scale));
  if (scale >= 1) return { width: w, height: h, rgb: img.rgb };
  const rgb = new Uint8Array(w * h * 3);
  for (let y = 0; y < h; y++) {
    const sy = Math.min(ih - 1, Math.floor(y / scale));
    for (let x = 0; x < w; x++) {
      const sx = Math.min(iw - 1, Math.floor(x / scale));
      const si = (sy * iw + sx) * 3;
      const di = (y * w + x) * 3;
      rgb[di] = img.rgb[si];
      rgb[di + 1] = img.rgb[si + 1];
      rgb[di + 2] = img.rgb[si + 2];
    }
  }
  return { width: w, height: h, rgb };
}

function rgbFill(r, g, b) {
  return `${r} ${g} ${b} rg\n`;
}

function fillRect(x, y, w, h, color) {
  const [r, g, b] = color;
  return `${rgbFill(r, g, b)}${x} ${y} ${w} ${h} re\nf\n`;
}

function strokeRect(x, y, w, h, color, width = 1) {
  const [r, g, b] = color;
  return `${width} w\n${rgbFill(r, g, b)}${x} ${y} ${w} ${h} re\nS\n`;
}

function parseFlow(rawLines, imagesById) {
  /** @type {object[]} */
  const flow = [];
  let inCover = false;
  /** @type {string[]} */
  let coverLines = [];

  for (const raw of Array.isArray(rawLines) ? rawLines : []) {
    if (raw === COVER_MARKER) {
      inCover = true;
      continue;
    }
    if (raw === PAGE_BREAK) {
      if (inCover && coverLines.length) {
        flow.push({ kind: "cover", lines: coverLines });
        coverLines = [];
        inCover = false;
      }
      flow.push({ kind: "pagebreak" });
      continue;
    }
    const s = String(raw ?? "");
    if (s.startsWith(IMAGE_PREFIX)) {
      const id = s.slice(IMAGE_PREFIX.length).trim();
      const img = imagesById[id];
      if (img && img.rgb) flow.push({ kind: "image", id, img });
      continue;
    }
    if (s.startsWith(SECTION_PREFIX)) {
      const title = toAsciiPdfText(s.slice(SECTION_PREFIX.length).trim());
      if (title) flow.push({ kind: "section", title });
      continue;
    }
    const ascii = toAsciiPdfText(s);
    if (!ascii) continue;
    if (inCover) coverLines.push(ascii);
    else {
      for (const wl of wrapLine(ascii, 92)) flow.push({ kind: "line", text: wl });
    }
  }
  if (inCover && coverLines.length) flow.push({ kind: "cover", lines: coverLines });
  return flow;
}

function splitFlowIntoUnits(blocks) {
  const units = [];
  let i = 0;
  while (i < blocks.length) {
    const b = blocks[i];
    if (b.kind === "cover" || b.kind === "pagebreak") {
      units.push({ type: "flow", blocks: [b] });
      i += 1;
      continue;
    }
    let imgAt = -1;
    for (let j = i; j < blocks.length; j++) {
      if (blocks[j].kind === "pagebreak" || blocks[j].kind === "cover") break;
      if (blocks[j].kind === "image") {
        imgAt = j;
        break;
      }
    }
    if (imgAt < 0) {
      units.push({ type: "flow", blocks: blocks.slice(i) });
      break;
    }
    let stepStart = i;
    for (let j = imgAt - 1; j >= i; j--) {
      if (blocks[j].kind === "section" && /^Step \d+ -/.test(blocks[j].title)) {
        stepStart = j;
        break;
      }
    }
    if (stepStart > i) units.push({ type: "flow", blocks: blocks.slice(i, stepStart) });
    units.push({ type: "stepWithImage", blocks: blocks.slice(stepStart, imgAt + 1) });
    i = imgAt + 1;
  }
  return units;
}

/**
 * @param {string[]} rawLines
 * @param {{ logo?: object, images?: Record<string,object>, headerRightLines?: string[] }} opts
 */
export function buildPermissionGuidePdf(rawLines, opts = {}) {
  const logo = opts.logo && opts.logo.rgb ? opts.logo : null;
  const imagesById = opts.images && typeof opts.images === "object" ? opts.images : {};
  const headerRightLines = (opts.headerRightLines || []).map(toAsciiPdfText);

  const pageWidth = 595;
  const pageHeight = 842;
  const marginX = 48;
  const contentWidth = pageWidth - marginX * 2;
  const lineH = 13;
  const headerH = 56;
  const footerH = 36;
  const bodyTopFirst = pageHeight - headerH - 24;
  const bodyTop = pageHeight - headerH - 16;
  const minY = footerH + 8;

  const flow = parseFlow(rawLines, imagesById);
  const units = splitFlowIntoUnits(flow);
  /** @type {Map<string, {width:number,height:number,rgb:Uint8Array}>} */
  const usedImages = new Map();

  function measureBlocks(blocks, imgMaxW = contentWidth) {
    let h = 0;
    for (const b of blocks) {
      if (b.kind === "section") h += 28;
      else if (b.kind === "line") h += lineH;
      else if (b.kind === "image") {
        const scaled = scaleImage(b.img, imgMaxW);
        if (scaled) h += scaled.height + 12;
      }
    }
    return h;
  }

  /** @type {{ draw: string[] }[]} */
  const pages = [];
  let draw = [];
  let y = bodyTopFirst;

  const flushPage = () => {
    if (draw.length) pages.push({ draw });
    draw = [];
    y = bodyTop;
  };

  const ensureSpace = (need) => {
    if (y - need < minY) flushPage();
  };

  const drawPageChrome = (pageIndex, total, isCoverPage = false) => {
    let c = "";
    c += fillRect(0, 0, pageWidth, pageHeight, BRAND.white);
    if (isCoverPage) {
      c += fillRect(0, pageHeight - 200, pageWidth, 200, BRAND.purpleDark);
      c += fillRect(0, pageHeight - 202, pageWidth, 3, BRAND.purple);
      return c;
    }
    c += fillRect(0, pageHeight - headerH, pageWidth, headerH, BRAND.purpleDark);
    c += fillRect(0, pageHeight - headerH - 2, pageWidth, 2, BRAND.purple);
    if (logo) {
      const lx = marginX;
      const ly = pageHeight - headerH + (headerH - logo.height) / 2;
      c += `q\n${logo.width} 0 0 ${logo.height} ${lx} ${ly} cm\n/ImLogo Do\nQ\n`;
    }
    const titleX = logo ? marginX + (logo?.width || 0) + 14 : marginX;
    const titleY = pageHeight - 28;
    c += "BT\n/F2 11 Tf\n";
    c += `${rgbFill(...BRAND.white)}${titleX} ${titleY} Td\n(${pdfEscapeLiteral("NFT Claim Hub")}) Tj\nET\n`;
    const sub = headerRightLines[0] || "Vault claimrewards setup";
    c += "BT\n/F1 9 Tf\n";
    c += `${rgbFill(...BRAND.white)}${titleX} ${titleY - 13} Td\n(${pdfEscapeLiteral(sub)}) Tj\nET\n`;
    c += fillRect(marginX, footerH - 4, pageWidth - marginX * 2, 1, BRAND.purple);
    c += "BT\n/F1 8 Tf\n";
    c += `${rgbFill(...BRAND.muted)}${pageWidth - marginX - 72} ${footerH - 18} Td\n(${pdfEscapeLiteral(`Page ${pageIndex + 1} of ${total}`)}) Tj\nET\n`;
    return c;
  };

  const placeSection = (title) => {
    ensureSpace(28);
    const barH = 22;
    const barY = y - barH;
    draw.push(fillRect(marginX, barY, contentWidth, barH, BRAND.purple));
    draw.push("BT\n/F2 11 Tf\n");
    draw.push(`${rgbFill(...BRAND.white)}${marginX + 8} ${barY + 6} Td\n(${pdfEscapeLiteral(title)}) Tj\nET\n`);
    y = barY - 8;
  };

  const placeLine = (text, indent = 0) => {
    ensureSpace(lineH);
    const x = marginX + indent;
    draw.push("BT\n/F1 10 Tf\n");
    draw.push(`${rgbFill(...BRAND.text)}${x} ${y - lineH + 3} Td\n(${pdfEscapeLiteral(text)}) Tj\nET\n`);
    y -= lineH;
  };

  const imageKey = (id) => String(id || "").replace(/[^a-zA-Z0-9]/g, "_");

  const placeImage = (block, maxW = contentWidth) => {
    const scaled = scaleImage(block.img, maxW);
    if (!scaled) return;
    const key = imageKey(block.id);
    usedImages.set(key, scaled);
    const gap = 12;
    ensureSpace(scaled.height + gap);
    const imgY = y - scaled.height;
    draw.push(strokeRect(marginX - 2, imgY - 2, scaled.width + 4, scaled.height + 4, BRAND.cardBorder, 0.8));
    draw.push(`q\n${scaled.width} 0 0 ${scaled.height} ${marginX} ${imgY} cm\n/Img_${key} Do\nQ\n`);
    y = imgY - gap;
    return scaled;
  };

  const renderCover = (lines) => {
    pages.push({ draw: [], isCover: true, coverLines: lines });
    y = bodyTopFirst;
  };

  const placeStepWithImage = (blocks) => {
    flushPage();
    let imgMaxW = contentWidth;
    let need = measureBlocks(blocks, imgMaxW);
    let avail = y - minY;
    if (need > avail) {
      const textH = blocks.filter((b) => b.kind === "section").length * 28 +
        blocks.filter((b) => b.kind === "line").length * lineH;
      const imgBlock = blocks.find((b) => b.kind === "image");
      if (imgBlock) {
        const full = scaleImage(imgBlock.img, contentWidth);
        if (full) {
          const maxImgH = Math.max(100, avail - textH - 20);
          const shrink = Math.min(1, maxImgH / full.height);
          imgMaxW = Math.max(180, Math.floor(contentWidth * shrink));
        }
      }
    }
    for (const b of blocks) {
      if (b.kind === "section") placeSection(b.title);
      else if (b.kind === "line") {
        const indent = b.text.startsWith("  ") ? 14 : 0;
        placeLine(b.text.trimStart(), indent);
      } else if (b.kind === "image") placeImage(b, imgMaxW);
    }
  };

  for (const unit of units) {
    if (unit.type === "stepWithImage") {
      placeStepWithImage(unit.blocks);
      continue;
    }
    for (const block of unit.blocks) {
      if (block.kind === "cover") {
        renderCover(block.lines);
        continue;
      }
      if (block.kind === "pagebreak") {
        flushPage();
        continue;
      }
      if (block.kind === "section") placeSection(block.title);
      else if (block.kind === "line") {
        const indent = block.text.startsWith("  ") ? 14 : 0;
        placeLine(block.text.trimStart(), indent);
      } else if (block.kind === "image") placeImage(block);
    }
  }
  if (draw.length || pages.length === 0) flushPage();

  const totalPages = pages.length;
  pages.forEach((p, idx) => {
    if (p.isCover) {
      let c = drawPageChrome(idx, totalPages, true);
      const lines = p.coverLines || [];
      let cy = pageHeight - 120;
      if (logo) {
        const lx = marginX;
        const ly = pageHeight - 100;
        c += `q\n${logo.width} 0 0 ${logo.height} ${lx} ${ly} cm\n/ImLogo Do\nQ\n`;
      }
      for (let i = 0; i < lines.length; i++) {
        const sz = i === 0 ? 18 : i === 1 ? 12 : 10;
        const font = i === 0 ? "F2" : "F1";
        const col = i < 2 ? BRAND.white : [0.85, 0.85, 0.9];
        c += `BT\n/${font} ${sz} Tf\n`;
        c += `${rgbFill(...col)}${marginX} ${cy} Td\n(${pdfEscapeLiteral(lines[i])}) Tj\nET\n`;
        cy -= i === 0 ? 26 : 16;
      }
      p.draw.unshift(c);
      return;
    }
    const chrome = drawPageChrome(idx, totalPages, false);
    p.draw.unshift(chrome);
  });

  const catalogObjNum = 1;
  const pagesObjNum = 2;
  const fontRegNum = 3;
  const fontBoldNum = 4;
  let nextObj = 5;

  const imageObjNums = new Map();
  if (logo) imageObjNums.set("logo", nextObj++);
  for (const key of usedImages.keys()) {
    if (!imageObjNums.has(key)) imageObjNums.set(key, nextObj++);
  }

  const firstPageObj = nextObj;
  nextObj += totalPages;
  const firstContentObj = nextObj;

  const objects = [];
  const addStr = (n, s) => objects.push({ n, b: encodeLatin1(s) });
  const addBytes = (n, parts) => objects.push({ n, b: concatBytes(parts) });

  addStr(catalogObjNum, `${catalogObjNum} 0 obj\n<< /Type /Catalog /Pages ${pagesObjNum} 0 R >>\nendobj\n`);
  const kids = [];
  for (let i = 0; i < totalPages; i++) kids.push(`${firstPageObj + i} 0 R`);
  addStr(pagesObjNum, `${pagesObjNum} 0 obj\n<< /Type /Pages /Kids [${kids.join(" ")}] /Count ${totalPages} >>\nendobj\n`);
  addStr(fontRegNum, `${fontRegNum} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`);
  addStr(fontBoldNum, `${fontBoldNum} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n`);

  const writeImg = (num, img) => {
    const hdr = `${num} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${img.width} /Height ${img.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Length ${img.rgb.length} >>\nstream\n`;
    addBytes(num, [encodeLatin1(hdr), img.rgb, encodeLatin1("\nendstream\nendobj\n")]);
  };

  if (logo) writeImg(imageObjNums.get("logo"), logo);
  for (const [key, num] of imageObjNums.entries()) {
    if (key === "logo") continue;
    const img = usedImages.get(key);
    if (img) writeImg(num, img);
  }

  const xName = new Map();
  let imi = 1;
  for (const [k, num] of imageObjNums.entries()) {
    xName.set(k, { num, name: k === "logo" ? "ImLogo" : `Im${imi++}` });
  }

  for (let pi = 0; pi < totalPages; pi++) {
    const pageNum = firstPageObj + pi;
    const contentNum = firstContentObj + pi;
    let content = pages[pi].draw.join("");
    content = content.replace(/\/Img_([a-zA-Z0-9_]+) Do/g, (_, id) => {
      const xo = xName.get(id);
      return xo ? `/${xo.name} Do` : "";
    });
    addStr(contentNum, `${contentNum} 0 obj\n<< /Length ${encodeLatin1(content).length} >>\nstream\n${content}\nendstream\nendobj\n`);
    const xobj = [];
    if (logo && xName.get("logo")) xobj.push(`/${xName.get("logo").name} ${imageObjNums.get("logo")} 0 R`);
    for (const [key, { name, num }] of xName.entries()) {
      if (key !== "logo") xobj.push(`/${name} ${num} 0 R`);
    }
    const xPart = xobj.length ? ` /XObject << ${xobj.join(" ")} >>` : "";
    addStr(
      pageNum,
      `${pageNum} 0 obj\n<< /Type /Page /Parent ${pagesObjNum} 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontRegNum} 0 R /F2 ${fontBoldNum} 0 R >>${xPart} >> /Contents ${contentNum} 0 R >>\nendobj\n`
    );
  }

  objects.sort((a, b) => a.n - b.n);
  const header = encodeLatin1("%PDF-1.3\n");
  let pos = header.length;
  const maxObj = firstContentObj + totalPages - 1;
  const xref = new Array(maxObj + 1).fill(0);
  const parts = [header];
  for (const o of objects) {
    xref[o.n] = pos;
    parts.push(o.b);
    pos += o.b.length;
  }
  const xrefStart = pos;
  let x = "xref\n";
  x += `0 ${maxObj + 1}\n0000000000 65535 f \n`;
  for (let n = 1; n <= maxObj; n++) x += `${String(xref[n] || 0).padStart(10, "0")} 00000 n \n`;
  parts.push(encodeLatin1(x));
  parts.push(encodeLatin1(`trailer\n<< /Size ${maxObj + 1} /Root ${catalogObjNum} 0 R >>\nstartxref\n${xrefStart}\n%%EOF\n`));
  return concatBytes(parts);
}

export const PERMISSION_GUIDE_IMAGE_IDS = [
  "claimrewards-advanced-permission",
  "claimrewards-link-auth",
  "claimrewards-verify-keys",
];
